import actions from 'src/modules/auth/authActions';
import AuthCurrentTenant from 'src/modules/auth/authCurrentTenant';
import MfaChallengeStorage from 'src/modules/auth/mfaChallengeStorage';

// MFA challenge fields reset to this whenever a session is established or the
// challenge is abandoned.
const emptyMfa = {
  mfaStep: 'password',
  mfaToken: null,
  mfaMaskedEmail: null,
  mfaExpiresAt: null,
  mfaResendCooldownUntil: null,
  mfaRemainingAttempts: null,
  mfaErrorMessage: null,
  loadingMfaVerify: false,
  loadingMfaResend: false,
};

// Resume an in-flight code challenge after a browser refresh on the code step.
const storedChallenge = MfaChallengeStorage.get();
const initialMfa = storedChallenge
  ? {
      ...emptyMfa,
      mfaStep: 'code',
      mfaToken: storedChallenge.mfaToken,
      mfaMaskedEmail: storedChallenge.maskedEmail,
      mfaExpiresAt: storedChallenge.expiresAt,
      mfaResendCooldownUntil:
        storedChallenge.resendCooldownUntil || null,
    }
  : { ...emptyMfa };

const initialData = {
  currentUser: null,
  currentTenant: null,
  loadingInit: true,
  loadingEmailConfirmation: false,
  loadingPasswordResetEmail: false,
  loadingPasswordChange: false,
  loadingVerifyEmail: false,
  loadingPasswordReset: false,
  loadingUpdateProfile: false,
  loading: false,
  errorMessage: null,
  errorMessageVerifyEmail: null,
  ...initialMfa,
};

export default (state = initialData, { type, payload }) => {
  if (type === actions.ERROR_MESSAGE_CLEARED) {
    return {
      ...state,
      errorMessage: null,
    };
  }

  if (type === actions.CURRENT_USER_REFRESH_SUCCESS) {
    return {
      ...state,
      currentUser: payload.currentUser || null,
      currentTenant: AuthCurrentTenant.selectAndSaveOnStorageFor(
        payload.currentUser,
      ),
    };
  }

  if (type === actions.CURRENT_USER_REFRESH_ERROR) {
    return {
      ...state,
      currentUser: null,
      currentTenant: AuthCurrentTenant.selectAndSaveOnStorageFor(
        null,
      ),
    };
  }

  if (type === actions.AUTH_START) {
    return {
      ...state,
      errorMessage: null,
      loading: true,
    };
  }

  if (type === actions.AUTH_SUCCESS) {
    return {
      ...state,
      currentUser: payload.currentUser || null,
      currentTenant: AuthCurrentTenant.selectAndSaveOnStorageFor(
        payload.currentUser,
      ),
      errorMessage: null,
      loading: false,
      ...emptyMfa,
    };
  }

  if (type === actions.MFA_CHALLENGE_START) {
    return {
      ...state,
      ...emptyMfa,
      errorMessage: null,
      loading: true,
    };
  }

  if (type === actions.MFA_CHALLENGE_SUCCESS) {
    return {
      ...state,
      ...emptyMfa,
      mfaStep: 'code',
      mfaToken: payload.mfaToken,
      mfaMaskedEmail: payload.maskedEmail,
      mfaExpiresAt: payload.expiresAt,
      mfaResendCooldownUntil:
        payload.resendCooldownUntil || null,
      loading: false,
      errorMessage: null,
    };
  }

  if (type === actions.MFA_CHALLENGE_ERROR) {
    return {
      ...state,
      loading: false,
      // Password-step (generic) error.
      errorMessage: payload || null,
    };
  }

  if (type === actions.MFA_VERIFY_START) {
    return {
      ...state,
      loadingMfaVerify: true,
      mfaErrorMessage: null,
    };
  }

  if (type === actions.MFA_VERIFY_ERROR) {
    return {
      ...state,
      loadingMfaVerify: false,
      mfaErrorMessage: (payload && payload.errorMessage) || null,
      mfaRemainingAttempts:
        payload && payload.remainingAttempts != null
          ? payload.remainingAttempts
          : state.mfaRemainingAttempts,
    };
  }

  if (type === actions.MFA_RESEND_START) {
    return {
      ...state,
      loadingMfaResend: true,
      mfaErrorMessage: null,
    };
  }

  if (type === actions.MFA_RESEND_SUCCESS) {
    return {
      ...state,
      loadingMfaResend: false,
      mfaExpiresAt: payload.expiresAt,
      mfaResendCooldownUntil:
        payload.resendCooldownUntil || null,
      mfaRemainingAttempts: null,
    };
  }

  if (type === actions.MFA_RESEND_ERROR) {
    return {
      ...state,
      loadingMfaResend: false,
      mfaResendCooldownUntil:
        payload && payload.cooldownSeconds
          ? Date.now() + payload.cooldownSeconds * 1000
          : state.mfaResendCooldownUntil,
    };
  }

  if (type === actions.MFA_RESET) {
    return {
      ...state,
      ...emptyMfa,
      loading: false,
      errorMessage: (payload && payload.errorMessage) || null,
    };
  }

  if (type === actions.AUTH_ERROR) {
    return {
      ...state,
      currentUser: null,
      currentTenant: null,
      errorMessage: payload || null,
      loading: false,
    };
  }

  if (type === actions.EMAIL_CONFIRMATION_START) {
    return {
      ...state,
      loadingEmailConfirmation: true,
    };
  }

  if (type === actions.EMAIL_CONFIRMATION_SUCCESS) {
    return {
      ...state,
      loadingEmailConfirmation: false,
    };
  }

  if (type === actions.EMAIL_CONFIRMATION_ERROR) {
    return {
      ...state,
      loadingEmailConfirmation: false,
    };
  }

  if (type === actions.PASSWORD_RESET_EMAIL_START) {
    return {
      ...state,
      loadingPasswordResetEmail: true,
    };
  }

  if (type === actions.PASSWORD_RESET_EMAIL_SUCCESS) {
    return {
      ...state,
      loadingPasswordResetEmail: false,
    };
  }

  if (type === actions.PASSWORD_RESET_EMAIL_ERROR) {
    return {
      ...state,
      loadingPasswordResetEmail: false,
    };
  }

  if (type === actions.UPDATE_PROFILE_START) {
    return {
      ...state,
      loadingUpdateProfile: true,
    };
  }

  if (type === actions.UPDATE_PROFILE_SUCCESS) {
    return {
      ...state,
      loadingUpdateProfile: false,
    };
  }

  if (type === actions.UPDATE_PROFILE_ERROR) {
    return {
      ...state,
      loadingUpdateProfile: false,
    };
  }

  if (type === actions.PASSWORD_CHANGE_START) {
    return {
      ...state,
      loadingPasswordChange: true,
    };
  }

  if (type === actions.PASSWORD_CHANGE_SUCCESS) {
    return {
      ...state,
      loadingPasswordChange: false,
    };
  }

  if (type === actions.PASSWORD_CHANGE_ERROR) {
    return {
      ...state,
      loadingPasswordChange: false,
    };
  }

  if (type === actions.AUTH_INIT_SUCCESS) {
    return {
      ...state,
      currentUser: payload.currentUser || null,
      currentTenant: AuthCurrentTenant.selectAndSaveOnStorageFor(
        payload.currentUser,
      ),
      loadingInit: false,
      // If silent auto-login succeeded, drop any stale challenge. Otherwise
      // keep it so a refresh on the code step resumes there.
      ...(payload.currentUser ? emptyMfa : {}),
    };
  }

  if (type === actions.AUTH_INIT_ERROR) {
    return {
      ...state,
      currentUser: null,
      currentTenant: null,
      loadingInit: false,
    };
  }

  if (type === actions.EMAIL_VERIFY_START) {
    return {
      ...state,
      loadingVerifyEmail: true,
      errorMessageVerifyEmail: null,
    };
  }

  if (type === actions.EMAIL_VERIFY_SUCCESS) {
    return {
      ...state,
      loadingVerifyEmail: false,
      errorMessageVerifyEmail: null,
    };
  }

  if (type === actions.EMAIL_VERIFY_ERROR) {
    return {
      ...state,
      loadingVerifyEmail: false,
      errorMessageVerifyEmail: payload,
    };
  }

  return state;
};
